import type { ReactNode } from 'react';
import { Button } from '@mantine/core';
import { IconExternalLink, IconMapPin } from '@tabler/icons-react';
import type { RidesCampusView } from '@actschurch/ccms-sdk';
import { campusAddressLine } from '@/components/rides/CampusPicker';
import '@/styles/flows.css';

/** Google Maps search link for a campus (coordinates first, then the street address). */
export function campusMapsUrl(c: RidesCampusView | undefined | null): string | null {
  if (!c) return null;
  if (c.latitude && c.longitude) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${c.latitude},${c.longitude}`)}`;
  }
  const address = campusAddressLine(c);
  if (!address) return null;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`Acts Church ${c.name}, ${address}`)}`;
}

interface Props {
  /** The pick-up campus from the lookups (may still be loading). */
  campus?: RidesCampusView | null;
  /** Shown while the lookups load, or when the campus is unknown. */
  campusName: string;
  /** Defaults to "Where to meet". */
  title?: ReactNode;
  /** One extra line under the instructions (e.g. "Be there 10 minutes early."). */
  note?: ReactNode;
}

/**
 * "Where to meet": the pick-up campus, its meeting-point instructions (RideCampus.pickupInstructions),
 * the street address and an "Open in Maps" button. Used on the booking confirmation and both
 * ride-day screens.
 */
export function WhereToMeet({ campus, campusName, title = 'Where to meet', note }: Props) {
  const address = campusAddressLine(campus);
  const instructions = campus?.pickupInstructions?.trim();
  const maps = campusMapsUrl(campus);
  return (
    <section className="rides-card rides-meet" aria-labelledby="rides-meet-title">
      <h2 id="rides-meet-title">{title}</h2>
      <div className="place">
        <IconMapPin size={22} aria-hidden="true" />
        <div style={{ minWidth: 0 }}>
          <b>{campus?.name ?? campusName}</b>
          {instructions && <p>{instructions}</p>}
          {address && <p className="addr">{address}</p>}
          {note && <p>{note}</p>}
        </div>
      </div>
      {maps && (
        <Button
          component="a"
          href={maps}
          target="_blank"
          rel="noopener noreferrer"
          variant="default"
          fullWidth
          className="maps"
          rightSection={<IconExternalLink size={18} aria-hidden="true" />}
        >
          Open in Maps
        </Button>
      )}
    </section>
  );
}
